import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, getOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { isAdmin } from "auth/tier";
import { isPositive } from "shared/trackers/money";
import { todayLocal } from "../../whiteboard/whiteboardTracking";
import { ALL_CLIENTS, type AgedInvoice, type ArAging, type ClientAging, type PaymentRecord } from "./receivables.types";

export interface PaymentForm {
  invoiceId: string;
  amount: string;
  receivedOn: string;
  method: string;
  reference: string;
  notes: string;
}

const emptyPaymentForm = (): PaymentForm => ({
  invoiceId: "",
  amount: "",
  receivedOn: todayLocal(),
  method: "",
  reference: "",
  notes: "",
});

/** Payments the Billing tab reads for one client, newest first; more is said, not hidden. */
export const PAYMENTS_LIMIT = 500;

/** The methods offered; any other text is kept as typed. */
export const PAYMENT_METHODS = ["ACH", "Wire", "Check", "Card", "Other"] as const;

type RawService = {
  service: (path: string) => {
    create: (data: object) => Promise<unknown>;
    remove: (id: string) => Promise<unknown>;
  };
};

/**
 * Receivables — what is owed by whom, how overdue, and what has been paid
 * (Blake's AR tracker in HQ, #3075 / #2663).
 *
 * ONE class, two registrations: `receivablesViewStore` (Billing →
 * Receivables, every client: `ar-aging` get('all')) and
 * `clientReceivablesViewStore` (Client 360 → Billing, pinned to the open
 * client by the ClientDetail route: get(<client id>), plus its payments).
 *
 * The report and the payments live in their ServiceStores (`ar-aging` keyed by
 * `key`, `invoice-payments`); this store holds which scope is open, how its
 * reads went, and the payment form. Every amount is the API's exact string —
 * nothing here adds two of them.
 *
 * Recording a payment goes through the RAW Feathers service, so a refusal
 * (a role, the client write gate, a draft invoice) reaches the form in the
 * API's words, and nothing is cached from the answer.
 */
export default class ReceivablesStore {
  rootStore: RootStore;

  /** `all`, or the open client's id. */
  scope: string = ALL_CLIENTS;
  loading = false;
  error: string | null = null;
  paymentsLoading = false;
  paymentsError: string | null = null;
  /** Billing → Receivables: list clients whose invoices are all settled too. */
  showSettled = false;

  paymentForm: PaymentForm = emptyPaymentForm();
  paymentSaving = false;
  paymentError: string | null = null;
  removingPaymentId: string | null = null;

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private store(path: string): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(path) as unknown as ServiceStoreType | null) ?? null;
  }

  private get agingService(): ServiceStoreType | null {
    return this.store("ar-aging");
  }

  private get paymentsService(): ServiceStoreType | null {
    return this.store("invoice-payments");
  }

  get isClientScope(): boolean {
    return this.scope !== ALL_CLIENTS;
  }

  /** The open scope's report, or null until it is read. */
  get report(): ArAging | null {
    const key = this.scope.toLowerCase();
    const rows = (this.agingService?.entities ?? []) as unknown as ArAging[];
    return rows.find((r) => String(r.key).toLowerCase() === key) ?? null;
  }

  /** Clients on the page: those still owing, or all with receivables when asked. */
  get clients(): ClientAging[] {
    const rows = this.report?.clients ?? [];
    return this.showSettled ? rows : rows.filter((c) => isPositive(c.balance));
  }

  /** How many clients the page is not showing because they owe nothing. */
  get settledCount(): number {
    return (this.report?.clients ?? []).filter((c) => !isPositive(c.balance)).length;
  }

  /** The open client's invoices, aged — keyed by invoice id, for the Billing tab's rows. */
  get agedInvoices(): Map<string, AgedInvoice> {
    return new Map((this.report?.invoices ?? []).map((i) => [String(i.invoiceId).toLowerCase(), i]));
  }

  agedInvoice(invoiceId: string): AgedInvoice | undefined {
    return this.agedInvoices.get(String(invoiceId).toLowerCase());
  }

  /** The invoices a payment can be recorded against: out, and still owing. */
  get payableInvoices(): AgedInvoice[] {
    return (this.report?.invoices ?? []).filter((i) => i.status !== "paid" && isPositive(i.balance));
  }

  /** The open client's payments, newest first. */
  get payments(): PaymentRecord[] {
    if (!this.isClientScope) return [];
    const id = this.scope.toLowerCase();
    return ((this.paymentsService?.entities ?? []) as unknown as PaymentRecord[])
      .filter((p) => String(p.clientId).toLowerCase() === id)
      .slice()
      .sort((a, b) => String(b.receivedOn).localeCompare(String(a.receivedOn)));
  }

  /** The payments read hit its cap: older ones exist and are not listed (the figures still cover them). */
  get paymentsCapped(): boolean {
    return this.payments.length >= PAYMENTS_LIMIT;
  }

  /** The web offers billing writes to administrators, as the Invoices page does; the API decides. */
  get canRecordPayments(): boolean {
    return isAdmin(this.rootStore);
  }

  get canRemovePayments(): boolean {
    return isAdmin(this.rootStore);
  }

  setShowSettled(value: boolean) {
    this.showSettled = value;
  }

  /**
   * Point the store at a scope and read it. A client's (Client 360 → Billing)
   * is read only on an instance billing is configured for — the tab does not
   * exist otherwise, and there is nothing to ask the API for.
   */
  async initialize(scope: string = ALL_CLIENTS): Promise<void> {
    this.scope = scope || ALL_CLIENTS;
    if (this.isClientScope) {
      const capabilities = (this.rootStore as unknown as { billingCapabilitiesViewStore?: { load: () => Promise<{ enabled: boolean }> } })
        .billingCapabilitiesViewStore;
      const enabled = await capabilities?.load().then((c) => c.enabled === true).catch(() => false);
      if (!enabled || this.scope !== scope) return;
    }
    await this.refresh();
  }

  /** Re-read the report (and, for a client, its payments). Never rejects. */
  async refresh(): Promise<void> {
    await Promise.all([this.load(), this.isClientScope ? this.loadPayments() : Promise.resolve()]);
  }

  async load(): Promise<void> {
    const scope = this.scope;
    const seq = ++this.seq;
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      await getOrThrow(this.agingService, scope);
    } catch (err) {
      if (seq !== this.seq || ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Receivables could not be loaded.");
      });
    } finally {
      if (seq === this.seq && !ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async loadPayments(): Promise<void> {
    const clientId = this.scope;
    const ended = sessionMark(this.rootStore);
    this.paymentsLoading = true;
    this.paymentsError = null;
    try {
      await findOrThrow(this.paymentsService, { query: { clientId, $limit: PAYMENTS_LIMIT, $sort: { receivedOn: -1 } } });
    } catch (err) {
      if (ended() || isSessionEnded(err) || clientId !== this.scope) return;
      runInAction(() => {
        this.paymentsError = loadErrorMessage(err, "Payments could not be loaded.");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.paymentsLoading = false;
        });
      }
    }
  }

  // ── recording a payment ──────────────────────────────────────────────────

  /** Open the form, on an invoice when one is named: the amount starts at what it still owes. */
  startPayment(invoiceId?: string) {
    const target = invoiceId ? this.agedInvoice(invoiceId) : this.payableInvoices[0];
    this.paymentForm = {
      ...emptyPaymentForm(),
      invoiceId: target?.invoiceId ?? "",
      amount: target ? trimZeros(target.balance) : "",
    };
    this.paymentError = null;
  }

  setPaymentField<K extends keyof PaymentForm>(field: K, value: PaymentForm[K]) {
    this.paymentForm = { ...this.paymentForm, [field]: value };
    if (field === "invoiceId") {
      const target = this.agedInvoice(String(value));
      if (target) this.paymentForm = { ...this.paymentForm, amount: trimZeros(target.balance) };
    }
    this.paymentError = null;
  }

  get isPaymentValid(): boolean {
    const f = this.paymentForm;
    return !!f.invoiceId && /^\d+(\.\d{1,4})?$/.test(f.amount.replace(/[$,\s]/g, "")) && isPositive(f.amount.replace(/[$,\s]/g, "")) && /^\d{4}-\d{2}-\d{2}$/.test(f.receivedOn) && !this.paymentSaving;
  }

  private get api(): RawService | undefined {
    return this.rootStore.app as unknown as RawService | undefined;
  }

  /** Record it. Resolves true when HQ took it; false with the API's reason on `paymentError`. */
  async savePayment(): Promise<boolean> {
    if (!this.isPaymentValid) {
      this.paymentError = "Pick an invoice, an amount of up to four decimal places and the day it arrived.";
      return false;
    }
    const ended = sessionMark(this.rootStore);
    this.paymentSaving = true;
    this.paymentError = null;
    const f = this.paymentForm;
    try {
      const api = this.api;
      if (!api) throw new Error("Not connected to the API — reload and try again.");
      await api.service("invoice-payments").create({
        invoiceId: f.invoiceId,
        amount: f.amount.replace(/[$,\s]/g, ""),
        receivedOn: f.receivedOn,
        method: f.method.trim() || null,
        reference: f.reference.trim() || null,
        notes: f.notes.trim() || null,
      });
      if (ended()) return false;
      runInAction(() => {
        this.paymentForm = emptyPaymentForm();
      });
      await this.refresh();
      return true;
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.paymentError = loadErrorMessage(err, "The payment could not be recorded.");
        });
      }
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.paymentSaving = false;
        });
      }
    }
  }

  /** Remove a payment (administrators; HQ keeps it, marked removed). */
  async removePayment(id: string): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.removingPaymentId = id;
    this.paymentsError = null;
    try {
      const api = this.api;
      if (!api) throw new Error("Not connected to the API — reload and try again.");
      await api.service("invoice-payments").remove(id);
      if (ended()) return false;
      await this.refresh();
      return true;
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.paymentsError = loadErrorMessage(err, "The payment could not be removed.");
        });
      }
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.removingPaymentId = null;
        });
      }
    }
  }

  /** Back to the constructed state (leaving the page, and sign-out). */
  reset() {
    this.seq += 1;
    this.scope = ALL_CLIENTS;
    this.loading = false;
    this.error = null;
    this.paymentsLoading = false;
    this.paymentsError = null;
    this.showSettled = false;
    this.paymentForm = emptyPaymentForm();
    this.paymentSaving = false;
    this.paymentError = null;
    this.removingPaymentId = null;
  }
}

/** "1250.5000" → "1250.5": what a person would type. */
export const trimZeros = (amount: string): string => amount.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
